import { Client } from "@microsoft/microsoft-graph-client";
import "isomorphic-fetch";

const GRAPH = "https://graph.microsoft.com/v1.0";

// Bearer-Header aus dem MSAL-Token. Für Binär-Operationen (Download/Upload)
// nutzen wir bewusst rohes fetch statt des Graph-SDK: Das SDK referenziert für
// Binärinhalte Node's `Buffer`, den es im Browser nicht gibt ("Buffer is not defined").
async function authHeader(getToken: () => Promise<string>): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getToken()}` };
}

export interface DriveFolderRef {
  driveId: string;
  itemId: string;
  webUrl: string;
  name: string;
}

export interface RemoteFile {
  id: string;
  driveId: string;
  name: string;
  size: number;
  mimeType: string;
  downloadUrl: string;
}

const TARGET_FOLDER = "00_Skaliert";
// Alle unterstützten Bildformate (identisch zum Lokal-Modus).
const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i;

export function createGraphClient(getToken: () => Promise<string>): Client {
  return Client.init({
    authProvider: async (done) => {
      try {
        const token = await getToken();
        done(null, token);
      } catch (err) {
        done(err as Error, null);
      }
    },
  });
}

// SharePoint-Sharing-URL → Graph "shares"-Resource via base64url-Encoding.
// https://learn.microsoft.com/graph/api/shares-get
function encodeSharingUrl(url: string): string {
  const base64 = btoa(url).replace(/=+$/, "").replace(/\//g, "_").replace(/\+/g, "-");
  return `u!${base64}`;
}

export async function resolveSharePointLink(
  client: Client,
  sharingUrl: string,
): Promise<DriveFolderRef> {
  const encoded = encodeSharingUrl(sharingUrl.trim());
  const item = await client.api(`/shares/${encoded}/driveItem`).get();

  if (!item.folder) {
    throw new Error("Der Link verweist nicht auf einen Ordner.");
  }

  return {
    driveId: item.parentReference.driveId,
    itemId: item.id,
    webUrl: item.webUrl,
    name: item.name,
  };
}

export async function listImagesInFolder(
  client: Client,
  folder: DriveFolderRef,
): Promise<RemoteFile[]> {
  const items: RemoteFile[] = [];
  let url = `/drives/${folder.driveId}/items/${folder.itemId}/children?$top=200&$select=id,name,size,file,@microsoft.graph.downloadUrl`;

  while (url) {
    const response = await client.api(url).get();
    for (const item of response.value) {
      if (!item.file) continue;
      if (!IMAGE_EXTENSIONS.test(item.name)) continue;
      items.push({
        id: item.id,
        driveId: folder.driveId,
        name: item.name,
        size: item.size ?? 0,
        mimeType: item.file.mimeType ?? "application/octet-stream",
        downloadUrl: item["@microsoft.graph.downloadUrl"],
      });
    }
    url = response["@odata.nextLink"];
  }

  return items;
}

export async function ensureScaledSubfolder(
  client: Client,
  folder: DriveFolderRef,
): Promise<DriveFolderRef> {
  try {
    const existing = await client
      .api(`/drives/${folder.driveId}/items/${folder.itemId}:/${TARGET_FOLDER}`)
      .get();
    return {
      driveId: folder.driveId,
      itemId: existing.id,
      webUrl: existing.webUrl,
      name: existing.name,
    };
  } catch (err: unknown) {
    const statusCode = (err as { statusCode?: number }).statusCode;
    if (statusCode !== 404) throw err;
  }

  const created = await client
    .api(`/drives/${folder.driveId}/items/${folder.itemId}/children`)
    .post({
      name: TARGET_FOLDER,
      folder: {},
      "@microsoft.graph.conflictBehavior": "fail",
    });

  return {
    driveId: folder.driveId,
    itemId: created.id,
    webUrl: created.webUrl,
    name: created.name,
  };
}

export async function downloadFile(
  getToken: () => Promise<string>,
  file: RemoteFile,
): Promise<ArrayBuffer> {
  // Über den authentifizierten Graph-/content-Endpunkt (graph.microsoft.com ist
  // CORS-fähig; die rohe CDN-downloadUrl wird oft geblockt und ist nur ~1 h gültig).
  const res = await fetch(`${GRAPH}/drives/${file.driveId}/items/${file.id}/content`, {
    headers: await authHeader(getToken),
  });
  if (!res.ok) {
    throw new Error(`Download fehlgeschlagen: ${res.status} ${res.statusText}`);
  }
  return await res.arrayBuffer();
}

export async function uploadScaledFile(
  getToken: () => Promise<string>,
  scaledFolder: DriveFolderRef,
  fileName: string,
  data: ArrayBuffer,
): Promise<void> {
  const path = `${GRAPH}/drives/${scaledFolder.driveId}/items/${scaledFolder.itemId}:/${encodeURIComponent(fileName)}:/content`;
  // Bei < 4 MB: einfacher PUT. Skalierte JPEGs sind in der Regel weit darunter,
  // aber große Originale (>4 MB nach Q80) sind möglich → Upload-Session-Fallback.
  if (data.byteLength < 4 * 1024 * 1024) {
    const res = await fetch(path, {
      method: "PUT",
      headers: { ...(await authHeader(getToken)), "Content-Type": "application/octet-stream" },
      body: data,
    });
    if (!res.ok) {
      throw new Error(`Upload fehlgeschlagen: ${res.status} ${res.statusText}`);
    }
    return;
  }

  await uploadLargeFile(getToken, scaledFolder, fileName, data);
}

async function uploadLargeFile(
  getToken: () => Promise<string>,
  scaledFolder: DriveFolderRef,
  fileName: string,
  data: ArrayBuffer,
): Promise<void> {
  const sessionRes = await fetch(
    `${GRAPH}/drives/${scaledFolder.driveId}/items/${scaledFolder.itemId}:/${encodeURIComponent(fileName)}:/createUploadSession`,
    {
      method: "POST",
      headers: { ...(await authHeader(getToken)), "Content-Type": "application/json" },
      body: JSON.stringify({ item: { "@microsoft.graph.conflictBehavior": "replace" } }),
    },
  );
  if (!sessionRes.ok) {
    throw new Error(`Upload-Session fehlgeschlagen: ${sessionRes.status} ${sessionRes.statusText}`);
  }
  const session = await sessionRes.json();

  const uploadUrl: string = session.uploadUrl;
  const CHUNK = 5 * 1024 * 1024; // 5 MB
  const total = data.byteLength;
  let offset = 0;

  while (offset < total) {
    const end = Math.min(offset + CHUNK, total);
    const chunk = data.slice(offset, end);
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Length": String(chunk.byteLength),
        "Content-Range": `bytes ${offset}-${end - 1}/${total}`,
      },
      body: chunk,
    });
    if (!response.ok && response.status !== 202) {
      throw new Error(`Chunk-Upload fehlgeschlagen: ${response.status}`);
    }
    offset = end;
  }
}
